import { useCallback, useEffect, useRef, useState } from "react";

const RATE_STORAGE_KEY = "blogVoiceReaderRate";
const VOICE_STORAGE_KEY = "blogVoiceReaderVoiceURI";
const VALID_RATES = new Set([0.75, 1, 1.25, 1.5]);
const ACTIVE_CHUNK_CLASS = "speech-reader-active-chunk";
const SELECTED_START_CLASS = "speech-reader-selected-start-chunk";

const getInitialRate = () => {
  if (typeof window === "undefined") return 1;

  const storedRate = Number(window.localStorage.getItem(RATE_STORAGE_KEY));
  return VALID_RATES.has(storedRate) ? storedRate : 1;
};

const getSpeechSynthesis = () => {
  if (typeof window === "undefined") return null;
  return window.speechSynthesis || null;
};

const isSpeechSupported = () =>
  typeof window !== "undefined" &&
  Boolean(window.speechSynthesis && window.SpeechSynthesisUtterance);

const sortVoices = (voices) => {
  const getVoicePriority = (voice) => {
    const lang = String(voice.lang || "").toLowerCase();
    if (lang === "en-in") return 0;
    if (lang === "en-us") return 1;
    if (lang === "en-gb") return 2;
    if (lang.startsWith("en-")) return 3;
    return 4;
  };

  return [...voices].sort((firstVoice, secondVoice) => {
    const priorityDifference =
      getVoicePriority(firstVoice) - getVoicePriority(secondVoice);

    if (priorityDifference !== 0) return priorityDifference;

    return String(firstVoice.name || "").localeCompare(
      String(secondVoice.name || ""),
    );
  });
};

const getInitialVoiceURI = () => {
  if (typeof window === "undefined") return "";
  return window.localStorage.getItem(VOICE_STORAGE_KEY) || "";
};

export function useSpeechReader({ chunks = [], postId } = {}) {
  const unsupported = !isSpeechSupported();
  const [status, setStatus] = useState(unsupported ? "unsupported" : "idle");
  const [activeChunkId, setActiveChunkId] = useState("");
  const [selectedStartChunkId, setSelectedStartChunkId] = useState("");
  const [rate, setRateState] = useState(getInitialRate);
  const [voices, setVoices] = useState([]);
  const [selectedVoiceURI, setSelectedVoiceURIState] =
    useState(getInitialVoiceURI);
  const [isAutoScrollEnabled, setIsAutoScrollEnabled] = useState(false);
  const activeElementRef = useRef(null);
  const selectedStartElementRef = useRef(null);
  const chunkIndexRef = useRef(0);
  const selectedStartIndexRef = useRef(0);
  const isStoppedRef = useRef(false);
  const playSessionRef = useRef(0);
  const rateRef = useRef(rate);
  const selectedVoiceURIRef = useRef(selectedVoiceURI);
  const chunksRef = useRef(chunks);
  const statusRef = useRef(status);

  useEffect(() => {
    chunksRef.current = chunks;
  }, [chunks]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  useEffect(() => {
    rateRef.current = rate;
    if (typeof window !== "undefined") {
      window.localStorage.setItem(RATE_STORAGE_KEY, String(rate));
    }
  }, [rate]);

  useEffect(() => {
    selectedVoiceURIRef.current = selectedVoiceURI;
    if (typeof window !== "undefined") {
      if (selectedVoiceURI) {
        window.localStorage.setItem(VOICE_STORAGE_KEY, selectedVoiceURI);
      } else {
        window.localStorage.removeItem(VOICE_STORAGE_KEY);
      }
    }
  }, [selectedVoiceURI]);

  useEffect(() => {
    const synthesis = getSpeechSynthesis();
    if (unsupported || !synthesis) return undefined;

    const loadVoices = () => {
      setVoices(sortVoices(synthesis.getVoices()));
    };

    loadVoices();

    if (typeof synthesis.addEventListener === "function") {
      synthesis.addEventListener("voiceschanged", loadVoices);
      return () => {
        synthesis.removeEventListener("voiceschanged", loadVoices);
      };
    }

    const previousHandler = synthesis.onvoiceschanged;
    synthesis.onvoiceschanged = loadVoices;

    return () => {
      synthesis.onvoiceschanged = previousHandler;
    };
  }, [unsupported]);

  const clearActiveChunk = useCallback(() => {
    if (activeElementRef.current) {
      activeElementRef.current.classList.remove(ACTIVE_CHUNK_CLASS);
      activeElementRef.current = null;
    }
    setActiveChunkId("");
  }, []);

  const clearSelectedStartChunk = useCallback(() => {
    if (selectedStartElementRef.current) {
      selectedStartElementRef.current.classList.remove(SELECTED_START_CLASS);
      selectedStartElementRef.current = null;
    }
    setSelectedStartChunkId("");
  }, []);

  const cancelPlayback = useCallback(() => {
    playSessionRef.current += 1;
    isStoppedRef.current = true;

    const synthesis = getSpeechSynthesis();
    if (synthesis) {
      synthesis.cancel();
    }

    chunkIndexRef.current = 0;

    if (activeElementRef.current) {
      activeElementRef.current.classList.remove(ACTIVE_CHUNK_CLASS);
      activeElementRef.current = null;
    }
  }, []);

  const markSelectedStartChunk = useCallback(
    (chunk) => {
      clearSelectedStartChunk();

      if (!chunk?.element) return;

      chunk.element.classList.add(SELECTED_START_CLASS);
      selectedStartElementRef.current = chunk.element;
      setSelectedStartChunkId(chunk.id);
    },
    [clearSelectedStartChunk],
  );

  const markActiveChunk = useCallback(
    (chunk) => {
      clearActiveChunk();

      if (!chunk?.element) return;

      chunk.element.classList.add(ACTIVE_CHUNK_CLASS);
      activeElementRef.current = chunk.element;
      setActiveChunkId(chunk.id);

      if (isAutoScrollEnabled) {
        chunk.element.scrollIntoView({ behavior: "smooth", block: "center" });
      }
    },
    [clearActiveChunk, isAutoScrollEnabled],
  );

  const stop = useCallback(
    (nextStatus = "stopped") => {
      cancelPlayback();
      selectedStartIndexRef.current = 0;
      setActiveChunkId("");
      clearSelectedStartChunk();
      setStatus(unsupported ? "unsupported" : nextStatus);
    },
    [cancelPlayback, clearSelectedStartChunk, unsupported],
  );

  const speakFromIndex = useCallback(
    (startIndex, sessionId) => {
      const synthesis = getSpeechSynthesis();
      const currentChunks = chunksRef.current;

      if (unsupported || !synthesis || currentChunks.length === 0) {
        setStatus(unsupported ? "unsupported" : "idle");
        clearActiveChunk();
        return;
      }

      if (sessionId !== playSessionRef.current || isStoppedRef.current) {
        return;
      }

      if (startIndex >= currentChunks.length) {
        chunkIndexRef.current = 0;
        clearActiveChunk();
        setStatus("idle");
        return;
      }

      const chunk = currentChunks[startIndex];
      const utterance = new window.SpeechSynthesisUtterance(chunk.text);
      const selectedVoice = currentChunks.length
        ? voices.find((voice) => voice.voiceURI === selectedVoiceURIRef.current)
        : null;
      if (selectedVoice) {
        utterance.voice = selectedVoice;
      }
      utterance.rate = rateRef.current;

      utterance.onstart = () => {
        if (sessionId !== playSessionRef.current || isStoppedRef.current) {
          return;
        }

        chunkIndexRef.current = startIndex;
        setStatus("playing");
        markActiveChunk(chunk);
      };

      utterance.onend = () => {
        if (sessionId !== playSessionRef.current || isStoppedRef.current) {
          return;
        }

        speakFromIndex(startIndex + 1, sessionId);
      };

      utterance.onerror = () => {
        if (sessionId !== playSessionRef.current || isStoppedRef.current) {
          return;
        }

        clearActiveChunk();
        setStatus("stopped");
      };

      synthesis.speak(utterance);
    },
    [clearActiveChunk, markActiveChunk, unsupported, voices],
  );

  const play = useCallback(() => {
    if (unsupported || chunksRef.current.length === 0) {
      setStatus(unsupported ? "unsupported" : "idle");
      return;
    }

    const synthesis = getSpeechSynthesis();
    if (!synthesis) return;

    synthesis.cancel();
    clearActiveChunk();
    isStoppedRef.current = false;
    chunkIndexRef.current = selectedStartIndexRef.current;
    const sessionId = playSessionRef.current + 1;
    playSessionRef.current = sessionId;
    setStatus("playing");
    speakFromIndex(selectedStartIndexRef.current, sessionId);
  }, [clearActiveChunk, speakFromIndex, unsupported]);

  const pause = useCallback(() => {
    if (unsupported) return;

    const synthesis = getSpeechSynthesis();
    if (!synthesis) return;

    synthesis.pause();
    setStatus("paused");
  }, [unsupported]);

  const resume = useCallback(() => {
    if (unsupported) return;

    const synthesis = getSpeechSynthesis();
    if (!synthesis) return;

    if (isStoppedRef.current) {
      isStoppedRef.current = false;
      const sessionId = playSessionRef.current + 1;
      playSessionRef.current = sessionId;
      setStatus("playing");
      speakFromIndex(chunkIndexRef.current, sessionId);
      return;
    }

    isStoppedRef.current = false;
    synthesis.resume();
    setStatus("playing");
  }, [speakFromIndex, unsupported]);

  const setRate = useCallback(
    (nextRate) => {
      const normalizedRate = Number(nextRate);
      const safeRate = VALID_RATES.has(normalizedRate) ? normalizedRate : 1;
      rateRef.current = safeRate;
      setRateState(safeRate);

      if (status === "playing") {
        const restartIndex = chunkIndexRef.current;
        const synthesis = getSpeechSynthesis();
        if (!synthesis) return;

        synthesis.cancel();
        clearActiveChunk();
        isStoppedRef.current = false;
        const sessionId = playSessionRef.current + 1;
        playSessionRef.current = sessionId;
        window.setTimeout(() => speakFromIndex(restartIndex, sessionId), 0);
      }
    },
    [clearActiveChunk, speakFromIndex, status],
  );

  const setSelectedVoiceURI = useCallback((voiceURI) => {
    setSelectedVoiceURIState(String(voiceURI || ""));
  }, []);

  const selectChunk = useCallback(
    (chunkId) => {
      const nextIndex = chunksRef.current.findIndex(
        (chunk) => chunk.id === chunkId,
      );

      if (nextIndex === -1) return false;

      const chunk = chunksRef.current[nextIndex];
      selectedStartIndexRef.current = nextIndex;
      chunkIndexRef.current = nextIndex;
      markSelectedStartChunk(chunk);

      if (statusRef.current === "playing") {
        const synthesis = getSpeechSynthesis();
        if (!synthesis) return true;

        synthesis.cancel();
        clearActiveChunk();
        isStoppedRef.current = false;
        const sessionId = playSessionRef.current + 1;
        playSessionRef.current = sessionId;
        setStatus("playing");
        window.setTimeout(() => speakFromIndex(nextIndex, sessionId), 0);
        return true;
      }

      if (statusRef.current === "paused") {
        const synthesis = getSpeechSynthesis();
        if (synthesis) {
          synthesis.cancel();
        }
        playSessionRef.current += 1;
        isStoppedRef.current = true;
        clearActiveChunk();
        setStatus("paused");
      }

      return true;
    },
    [clearActiveChunk, markSelectedStartChunk, speakFromIndex],
  );

  useEffect(() => {
    stop("idle");
    selectedStartIndexRef.current = 0;
    clearSelectedStartChunk();
  }, [clearSelectedStartChunk, postId, stop]);

  useEffect(() => {
    return () => {
      cancelPlayback();
      if (selectedStartElementRef.current) {
        selectedStartElementRef.current.classList.remove(SELECTED_START_CLASS);
      }
    };
  }, [cancelPlayback]);

  return {
    status,
    unsupported,
    activeChunkId,
    selectedStartChunkId,
    rate,
    setRate,
    voices,
    selectedVoiceURI,
    setSelectedVoiceURI,
    isAutoScrollEnabled,
    setIsAutoScrollEnabled,
    play,
    pause,
    resume,
    stop,
    selectChunk,
  };
}
